// src/components/HeaderActions.tsx
import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Dropdown, Avatar, Tooltip, App as AntdApp } from "antd";
import type { MenuProps } from "antd";
import { BellOutlined, LogoutOutlined, UserOutlined, MessageOutlined } from "@ant-design/icons";
import ProfileModal, { UserProfile } from "./ProfileModal";
import CurrencyRateWidget from "./CurrencyRateWidget";
import { useAppTheme } from "../theme/AppThemeProvider";
import { logout } from "../services/auth";
import { useNavigate } from "react-router-dom";

interface HeaderActionsProps {
  ordersCount?: number;
  onOpenNotifications?: () => void;
  messagesCount?: number;
  onOpenMessages?: () => void;
  onLogout?: () => void;
}

const AVATAR_KEY = "userAvatar";
const PROFILE_KEY = "userProfile";

function loadProfile(): UserProfile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const HeaderActions: React.FC<HeaderActionsProps> = ({
  ordersCount = 0,
  onOpenNotifications,
  messagesCount = 0,
  onOpenMessages,
  onLogout,
}) => {
  const [avatar, setAvatar] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({});
  const fileRef = useRef<HTMLInputElement | null>(null);

  const { setMode } = useAppTheme();
  const navigate = useNavigate();

  // загрузка аватара/профиля и применения темы
  useEffect(() => {
    const saved = localStorage.getItem(AVATAR_KEY);
    if (saved) setAvatar(saved);
    const p = loadProfile();
    setProfile(p);
    if (p?.theme === "dark" || p?.theme === "light") setMode(p.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickFile = () => fileRef.current?.click();
  const { message } = AntdApp.useApp();
  
  const handleFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = String(reader.result || "");
      setAvatar(base64);
      localStorage.setItem(AVATAR_KEY, base64);
      message.success("Аватар обновлён");
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const handleLogout = () => {
    try {
      onLogout?.(); // если родитель хочет реагировать — даём шанс
      logout();     // чистит токен + служебные ключи в auth.ts (уже выровнено)
      // доп. ручная очистка authToken не нужна — logout() делает это сам
    } finally {
      // единый корректный переход внутри SPA
      navigate("/login", { replace: true });
    }
  };

  const menuItems: MenuProps["items"] = [
    { key: "profile", label: "Профиль" },
    { type: "divider" },
    { key: "upload", label: "Загрузить фото" },
  ];

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "upload") handlePickFile();
    if (key === "profile") setProfileOpen(true);
  };

  return (
    <div className="header-icons">
      {/* Курс валют USD / AMD */}
      <CurrencyRateWidget />

      {/* Сообщения покупателей */}
      <Tooltip title="Сообщения">
        <Badge count={messagesCount} overflowCount={99} size="small" className="header-icon">
          <Button type="text" icon={<MessageOutlined />} onClick={onOpenMessages} />
        </Badge>
      </Tooltip>

      {/* Оповещения о заказах */}
      <Tooltip title="Оповещения о заказах">
        <Badge count={ordersCount} overflowCount={99} size="small" className="header-icon">
          <Button type="text" icon={<BellOutlined />} onClick={onOpenNotifications} />
        </Badge>
      </Tooltip>

      {/* Аватар / меню профиля */}
      <Dropdown menu={{ items: menuItems, onClick: onMenuClick }} trigger={["click"]}>
        <Avatar
          src={avatar || undefined}
          icon={!avatar ? <UserOutlined /> : undefined}
          size={36}
          className="header-icon header-avatar"
          style={{ cursor: "pointer" }}
        />
      </Dropdown>

      {/* Выход */}
      <Tooltip title="Выход">
        <Button
          type="text"
          icon={<LogoutOutlined />}
          className="header-icon"
          onClick={handleLogout}
          aria-label="Выйти"
        />
      </Tooltip>

      {/* Модалка профиля */}
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        initial={profile}
        avatar={avatar}
        onPickAvatar={handlePickFile}
        onSave={(p) => {
          setProfile(p);
          localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
          if (p.theme === "dark" || p.theme === "light") setMode(p.theme);
          message.success("Профиль сохранён");
          setProfileOpen(false);
        }}
      />

      {/* Скрытый input для загрузки аватара */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default HeaderActions;
